import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import DetectRTC from 'detectrtc';

import { useShallow } from 'zustand/react/shallow';

import { useVideoStreamStore } from '@src/stores';

import { getDataTimeString, saveDataToFile } from '@src/helpers';

import { IVideoStream } from '@src/types';
import { useRoomContext } from '@src/contexts';

const useVideoContextMenu = (videoStream: IVideoStream, videoRef: React.RefObject<HTMLVideoElement>) => {
  const [isMirrorActive, setIsMirrorActive] = useState(
    () => !!videoStream.isCurrentUser && videoStream.type === 'videoType'
  );
  const [isFullscreenActive, setIsFullscreenActive] = useState(false);
  const canvasRef = useRef<HTMLCanvasElement>(null);

  const { videoStreams, updateVideo, pip, setPip } = useVideoStreamStore(
    useShallow((state) => ({
      videoStreams: state.videoStreams,
      updateVideo: state.updateVideo,
      setPip: state.setPip,
      pip: state.pip,
    }))
  );
  const newPinnedBox = useMemo(() => videoStreams.find(({ isPinned }) => !!isPinned), [videoStreams]);

  const { isMobileDevice } = useRoomContext();

  const checkIfPipActivated = useCallback(() => {
    return document.pictureInPictureElement?.id === videoRef.current?.id;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const checkIfFullscreenActive = useCallback(() => {
    return document.fullscreenElement === videoRef.current;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handlePictureInPicture = useCallback(() => {
    if (videoRef.current) {
      if (!checkIfPipActivated()) {
        if (videoRef.current.requestPictureInPicture) {
          videoRef.current
            .requestPictureInPicture()
            .then(() => {
              setPip({ isActive: true, activeId: videoRef.current?.id || '' });
            })
            .catch((error) => {
              console.error('Failed to enter Picture-in-Picture mode:', error);
            });
        }
      } else {
        document
          .exitPictureInPicture()
          .then(() => {
            setPip({ isActive: false, activeId: '' });
          })
          .catch((error) => {
            console.error('Failed to exit Picture-in-Picture mode:', error);
          });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [checkIfPipActivated]);

  const handleFullScreen = useCallback(() => {
    if (checkIfFullscreenActive()) {
      // If already in full-screen, exit full-screen
      document.exitFullscreen().catch((error) => {
        console.error('Failed to exit full-screen mode:', error);
      });
    } else {
      // If not in full-screen, request full-screen
      if (videoRef.current) {
        videoRef.current.requestFullscreen().catch((error) => {
          console.error('Failed to enter full-screen mode:', error);
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSnapshot = useCallback(() => {
    if (videoRef.current && canvasRef.current) {
      const canvas = canvasRef.current;
      const context = canvas.getContext('2d');

      if (context) {
        // Set canvas dimensions to match the video dimensions
        canvas.width = videoRef.current.videoWidth;
        canvas.height = videoRef.current.videoHeight;

        // Draw the current frame of the video on the canvas
        context.drawImage(videoRef.current, 0, 0, canvas.width, canvas.height);

        // Convert canvas content to a data URL (base64 encoded image)
        const imageUrl = canvas.toDataURL('image/png'); // You can also use 'image/jpeg'

        saveDataToFile(imageUrl, getDataTimeString() + '-SNAPSHOT.png');
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleVideoMirror = useCallback(() => {
    setIsMirrorActive((prev) => !prev);
  }, []);

  const handlePinVideo = useCallback(() => {
    const currentPinnedVideo = videoStreams.find(({ type, isPinned }) => !!isPinned && type === 'videoType');
    if (currentPinnedVideo) {
      updateVideo(currentPinnedVideo.id, { isPinned: false });
    }
    updateVideo(videoStream.id, { isPinned: !videoStream.isPinned });
    if (DetectRTC.isMobileDevice && DetectRTC.osName === 'iOS') {
      document
        .exitPictureInPicture()
        .then(() => {
          setPip({ isActive: false, activeId: '' });
        })
        .catch((e) => {
          console.error(e);
        });
    }
  }, [updateVideo, videoStream.id, videoStream.isPinned, videoStreams, setPip]);

  useEffect(() => {
    const handlePiPChange = () => {
      setPip({ isActive: false, activeId: '' });

      if (isMobileDevice) {
        setTimeout(() => {
          videoRef.current?.play();
        }, 300);
      }
    };

    const restartPip = async () => {
      const video = videoRef?.current;
      if (!video) {
        console.log('[pip] video element not found');
        setPip({ isActive: false, activeId: '' });
        return;
      }

      if (video.readyState < 1) {
        await new Promise((resolve) => video.addEventListener('loadedmetadata', resolve, { once: true }));
      }

      try {
        if (pip.activeId === video.id) {
          await video
            ?.requestPictureInPicture()
            .then(() => {
              setPip({ isActive: true, activeId: video.id });
            })
            .catch(() => {
              setPip({ isActive: false, activeId: '' });
            });
        }
      } catch (error) {
        console.error('Failed to enter Picture-in-Picture mode:', error);
        setPip({ isActive: false, activeId: '' });
      }
    };

    const video = videoRef?.current;
    if (!video) return;
    if (document.pictureInPictureElement) {
      restartPip();
    }
    video.addEventListener('leavepictureinpicture', handlePiPChange);
    document.addEventListener('visibilitychange ', handlePiPChange);
    return () => {
      video.removeEventListener('leavepictureinpicture', handlePiPChange);
      document.removeEventListener('visibilitychange ', handlePiPChange);
    };
  }, [isMobileDevice, newPinnedBox, pip.activeId, setPip, videoRef]);

  useEffect(() => {
    if (DetectRTC.isMobileDevice && DetectRTC.osName === 'iOS') {
      document
        .exitPictureInPicture()
        .then(() => {
          setPip({ isActive: false, activeId: '' });
        })
        .catch((e) => {
          console.error(e);
        });
    }
  }, [newPinnedBox, setPip]);

  useEffect(() => {
    // Listen to full-screen state change events
    const handleFullScreenChange = () => {
      setIsFullscreenActive(!checkIfFullscreenActive());
    };

    // Add event listener for full-screen state changes
    document.addEventListener('fullscreenchange', handleFullScreenChange);

    return () => {
      // Cleanup the event listener when the component unmounts
      document.removeEventListener('fullscreenchange', handleFullScreenChange);
    };
  }, [checkIfFullscreenActive]);

  useEffect(() => {
    setIsMirrorActive(!!videoStream.isCurrentUser && videoStream.type === 'videoType');
  }, [videoStream.isCurrentUser, videoStream.type]);

  return {
    canvasRef,
    isMirrorActive,
    isFullscreenActive,

    handlePinVideo,
    handleSnapshot,
    handleFullScreen,
    handleVideoMirror,
    handlePictureInPicture,
  };
};

export default useVideoContextMenu;
